/**
 * Which History view is on screen — Readings, Journal, or the flagged Day.
 *
 * ── A CHOICE, NOT A ROUTE ───────────────────────────────────────────────────
 *
 * Readings and Journal are the same page, the same `GET /users/{id}/recordings` query and
 * the same pagination (journal concept §B): only the columns fork. So the choice
 * is component state remembered per browser, rather than a second route or a
 * query parameter — there is nothing to link to that the other view does not
 * already show. Day is a filtered read of one recorder-local date (EXP-003).
 *
 * ── THE KEY'S NAMESPACE IS `brainspot:` ─────────────────────────────────────
 *
 * `ThemeContext.tsx` writes `brainspot:theme`, and that is the convention every
 * key this app owns follows. (The sprint spec's `bs_web_theme` names a key that
 * does not exist in this codebase.) One key, one preference, and nothing about
 * a user's data in it.
 *
 * ── AN UNREADABLE STORE IS NOT AN ERROR ─────────────────────────────────────
 *
 * Private mode and a blocked store both throw on access, and a value written by
 * a future build may be a word this one does not know. Either way the answer is
 * the default view, for the reason `readStoredPreference` gives one line over:
 * a remembered preference is a convenience and never a precondition.
 */

import { EXPERIMENTS_ENABLED } from './features'

export type HistoryView = 'readings' | 'journal' | 'day'

export const HISTORY_VIEW_STORAGE_KEY = 'brainspot:history-view'

/**
 * Readings first, because it is the view that was there before this one and
 * the one every existing link, screenshot and runbook describes.
 */
export const DEFAULT_HISTORY_VIEW: HistoryView = 'readings'

/**
 * The two segments, in the concept's order.
 *
 * Structurally a `SegmentedOption<HistoryView>` without importing one: this
 * module is in `lib/` and `lib/` does not reach into `components/`.
 */
export const HISTORY_VIEW_OPTIONS: readonly { value: HistoryView; label: string }[] = [
  { value: 'readings', label: 'Readings' },
  { value: 'journal', label: 'Journal' },
  ...(EXPERIMENTS_ENABLED ? [{ value: 'day' as const, label: 'Day' }] : []),
]

/** Narrowed from the stored string, never cast: an unknown word is the default. */
export function readStoredHistoryView(): HistoryView {
  try {
    const raw = localStorage.getItem(HISTORY_VIEW_STORAGE_KEY)
    if (raw === 'readings' || raw === 'journal') return raw
    if (EXPERIMENTS_ENABLED && raw === 'day') return raw
  } catch {
    // Private mode, or storage disabled.
  }
  return DEFAULT_HISTORY_VIEW
}

export function storeHistoryView(view: HistoryView): void {
  try {
    localStorage.setItem(HISTORY_VIEW_STORAGE_KEY, view)
  } catch {
    // Not fatal: the view still switches for this visit.
  }
}

/* ── The Journal view's second control: how the days are gathered ─────────── */

/**
 * `By day · By week · By month` (concept §B2/§B3).
 *
 * ── THREE SEGMENTS, NOT TWO ─────────────────────────────────────────────────
 *
 * The concept draws the control as *By week · By month*, and that is two
 * segments for THREE states: the ungrouped day list is the Journal view's
 * starting state, it has a footer and a coverage line the grouped views do not,
 * and `SegmentedControl` has no deselect -- `value` can be `null`, but no
 * button sets it back to `null`. Two segments would therefore have made the
 * state the view OPENS in unreachable once a reader left it. `'day'` is that
 * state, named.
 *
 * It is not the same question as `HistoryView`, so it is not the same key:
 * switching to Readings and back leaves the grouping where a reader put it.
 */
export type JournalGroupingChoice = 'day' | 'week' | 'month'

export const JOURNAL_GROUPING_STORAGE_KEY = 'brainspot:journal-grouping'

/** Day rows, as the Journal view has always opened (concept §B). */
export const DEFAULT_JOURNAL_GROUPING: JournalGroupingChoice = 'day'

export const JOURNAL_GROUPING_OPTIONS: readonly { value: JournalGroupingChoice; label: string }[] = [
  { value: 'day', label: 'By day' },
  { value: 'week', label: 'By week' },
  { value: 'month', label: 'By month' },
]

/** Narrowed from the stored string, never cast: an unknown word is the default. */
export function readStoredJournalGrouping(): JournalGroupingChoice {
  try {
    const raw = localStorage.getItem(JOURNAL_GROUPING_STORAGE_KEY)
    if (raw === 'day' || raw === 'week' || raw === 'month') return raw
  } catch {
    // Private mode, or storage disabled.
  }
  return DEFAULT_JOURNAL_GROUPING
}

export function storeJournalGrouping(grouping: JournalGroupingChoice): void {
  try {
    localStorage.setItem(JOURNAL_GROUPING_STORAGE_KEY, grouping)
  } catch {
    // Not fatal: the grouping still switches for this visit.
  }
}
